import React, { useEffect } from "react";
import { Table, message, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { useDiscernModel } from "../discernModel";
import { IDiscernDetails, discernService, IDiscernSeachFields } from "../discernService";
import { CheckCircleTwoTone, LinkOutlined, StopOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useNavModel } from "@/model/navModel";
import { globalPrompt } from "@/components/message";
import { SorterResult } from "antd/es/table/interface";


export function DataTable() {
    const {
        dataSource, selectedRowKeys, pgCfg, searchParam,
        loadData, setSelectedRowKeys
    } = useDiscernModel();
    const { addNav, findNav, switchNavState } = useNavModel(() => []);

    const onEdit = (record: IDiscernDetails) => {
        let customerEditKey = "3-2-1";
        let isFindNav = findNav(customerEditKey);
        if (!isFindNav) {
            addNav(customerEditKey, { id: record.id ?? "", mode: "edit" });
        } else {
            switchNavState(customerEditKey, { id: record.id ?? "", mode: "edit" });
        }
    }

    const onCancel = (id: string) => {
        globalPrompt("modal", {
            title: "确认丢弃？",
            text: "",
            onOk: () => discernService.distinguishCancel(id).then(res => {
                res && loadData();
                message.success("作废成功");
            })
        });
    }

    const columns: ColumnsType<IDiscernDetails> = [
        {
            title: '序号',
            width: 100,
            align: "center",
            render: (t, r, i) => i + 1
        },
        {
            title: '标题',
            align: "left",
            dataIndex: 'title',
            width: 310,
            render: (text, recrod) => <Tooltip placement="topLeft" title={text} arrowPointAtCenter>
                <p
                    className="ellipsis"
                    style={{ color: "#00f" }}
                    onClick={() => onEdit(recrod)}
                >{text}</p>
            </Tooltip>
        },
        {
            title: '来源',
            align: "center",
            dataIndex: 'sourceFrom',
        },
        {
            title: '地区',
            align: "center",
            dataIndex: 'region',
        },
        {
            title: '发布日期',
            align: "center",
            dataIndex: 'pubTime',
            sorter: {
                multiple: 2
            },
            render: (text) => {
                if (text && text.length > 10) {
                    return text.substring(0, 10);
                };
                return text;
            }
        },
        {
            title: '截止日期',
            align: "center",
            dataIndex: 'bidEndTime',
            sorter: {
                multiple: 1
            },
            render: (text) => {
                if (text && text.length > 10) {
                    return text.substring(0, 10);
                };
                return text;
            }
        },
        {
            title: '是否参与',
            align: "center",
            dataIndex: 'isJoin',
            render: text => text ? +(text ?? 0) ? "是" : "否" : ""
        },
        {
            title: '公告类型',
            align: "center",
            dataIndex: 'noticeType',
        },
        {
            title: '项目编码',
            align: "center",
            dataIndex: 'code',
        },
        {
            title: '关键词',
            align: "center",
            dataIndex: 'keywords',
        },
        {
            title: '等级',
            align: "center",
            dataIndex: 'grade',
        },
        {
            title: '采集日期',
            align: "center",
            dataIndex: 'createTime',
        },
        {
            title: '标记时间',
            align: "center",
            dataIndex: 'tagTime',
        },
        {
            title: '标注状态',
            width: 60,
            align: "center",
            fixed: "right",
            className: "td-wrap",
            dataIndex: "status",
            render: text => +(text ?? 0) ? <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: "20px" }} /> : <InfoCircleOutlined twoToneColor="#333" style={{ fontSize: "20px" }} />
        },
        {
            title: '操作',
            width: 120,
            align: "center",
            fixed: "right",
            className: "td-wrap",
            render: (text, record) => <>
                <LinkOutlined
                    style={{ fontSize: "20px" }}
                    onClick={() => onEdit(record)}
                />
                <StopOutlined
                    style={{ fontSize: "19px", verticalAlign: "top", marginLeft: "10px" }}
                    onClick={() => onCancel(record.id)}
                />
            </>
        }
    ];


    useEffect(() => {
        loadData();
    }, [])

    //点击行选高亮
    const onRow = (record: IDiscernDetails) => {
        let findObj = selectedRowKeys.find(item => item.id === record.id)
        if (findObj) {
            let newSelectedRowKeys = selectedRowKeys.filter(item => item.id !== record.id);
            setSelectedRowKeys(newSelectedRowKeys);
        } else {
            setSelectedRowKeys(origin => [...origin, record]);
        }
    };

    /**
    * 表格多重表头排序
    * 1.排序  ascend 向上  descend 向下
    */
    function onChange(pagination: any, filters: any, sorter: SorterResult<IDiscernDetails> | SorterResult<any>[], extra: any) {
        let obj: IDiscernSeachFields = {};
        //多个搜索
        if (Array.isArray(sorter)) {
            sorter.forEach(item => {
                let { field, order, ...props } = item;
                if (order === "descend") {
                    obj[`${field ?? ""}Desc`] = 1
                } else if (order === "ascend") {
                    obj[`${field ?? ""}`] = 1
                }
            })
        } else {
            let { field, order, ...props } = sorter;
            if (order === "descend") {
                obj[`${field ?? ""}Desc`] = 1
            } else if (order === "ascend") {
                obj[`${field ?? ""}`] = 1
            }
        }
        loadData({ pageNum: pagination.current, pageSize: pagination.pageSize }, { ...searchParam, ...obj })
    }

    return (
        <Table
            rowKey={record => record.id}
            rowSelection={{
                //全部选中时 (存row[])
                onSelectAll: (selected, selectedRows, changeRows) => {
                    selected ? setSelectedRowKeys((origin) => [...origin, ...changeRows])
                        : setSelectedRowKeys((origin) => {
                            let changeRowId = changeRows.map(item => item.id);
                            return origin.filter(item => changeRowId.indexOf(item.id) < 0)
                        });
                },
                onSelect: (record, selected, selectedRows) => onRow(record),
                selectedRowKeys: selectedRowKeys.map(item => item.id)
            }}
            columns={columns}
            dataSource={dataSource}
            pagination={{
                ...pgCfg,
                position: ["bottomCenter"],
                showTotal: () => `共${pgCfg.total}条`,
                onChange: (pageNum, pageSize) => {
                    // loadData({ pageNum, pageSize })
                }
            }}
            onRow={record => ({
                onClick: () => onRow(record),
                onContextMenu: (e) => {
                    e.preventDefault();
                    setSelectedRowKeys([record]);
                }
            })}
            onChange={onChange}
            scroll={{ x: 'max-content' }}
        />
    )
}
